import { useState, useEffect, useMemo } from 'react';
import { parseMarkdown, sortLessonsByNumber, getNextLesson, getPreviousLesson } from '../utils/markdownParser';

// Function to load lesson files from manifest
const loadLessonManifest = async () => {
  try {
    const response = await fetch('/content/lessons.json');
    if (!response.ok) {
      throw new Error('Failed to load lessons manifest');
    }
    const lessons = await response.json();
    console.log('Loaded lesson manifest:', lessons);
    return lessons;
  } catch (err) {
    console.error('Failed to load lesson manifest, falling back to discovery:', err);
    // Fallback to discovery if manifest fails
    return await discoverLessonFiles();
  }
};

// Fallback function to discover lesson files by systematic probing
const discoverLessonFiles = async () => {
  console.log('Discovering lesson files...');
  const discoveredFiles = [];
  
  // Try known lesson files first
  const knownLessons = [
    'Lesson 1.1  Role of the AI Solution Architect.md',
    'Lesson 1.2  Anatomy of an AI System.md',
    'Lesson 1.3  Generative AI Use Cases.md',
    'Lesson 2.1  Supervised Learning Patterns.md',
    'Lesson 2.2  Unsupervised Learning & Clustering.md',
    'Lesson 2.3  Model Evaluation & Metrics.md',
    'Lesson 2.4  Training Pipelines.md',
    'Lesson 3.1  Neural Network Basics.md',
    'Lesson 3.2  Convolutional & Recurrent Neural Networks.md',
    'Lesson 3.3  Transformers & Attention.md',
    'Lesson 3.4  Tools & Frameworks.md',
    'Lesson 4.1  Autoencoders & VAEs.md',
    'Lesson 4.2  Generative Adversarial Networks (GANs).md',
    'Lesson 4.3  Diffusion Models.md',
    'Lesson 4.4  Model Selection & Trade-offs.md',
    'Lesson 5.1  LLM Architectures & Pretraining.md',
    'Lesson 5.2  Prompt Engineering Fundamentals.md',
    'Lesson 5.3  Fine-tuning & RLHF.md',
    'Lesson 5.4  Building a Chatbot Service.md',
    'Lesson 6.1  Data Preprocessing & Feature Stores.md',
    'Lesson 6.2  Data Labeling & Augmentation.md',
    'Lesson 6.3  Orchestration & Scheduling.md',
    'Lesson 7.1  Deploying AI & GenAI Models in Production.md',
    'Lesson 7.2  Scalability & Serverless Inference.md',
    'Lesson 7.3  CI CD for Models.md',
    'Lesson 7.4  Monitoring & Drift Detection.md',
    'Lesson 7.5  Cloud AI Services Integration.md',
    'Lesson 8.1  The Cutting Edge - What\'s New in AI.md',
    'Lesson 8.2 Security for AI.md'
  ];
  
  // Test each known lesson file
  for (const filename of knownLessons) {
    try {
      const response = await fetch(`/content/${filename}`, { method: 'HEAD' });
      if (response.ok) {
        discoveredFiles.push(filename);
      }
    } catch (err) {
      // File doesn't exist, continue
    }
  }
  
  // Probe for additional chapters beyond what we know
  for (let chapter = 9; chapter <= 12; chapter++) {
    for (let lesson = 1; lesson <= 5; lesson++) {
      const patterns = [
        `Lesson ${chapter}.${lesson}  `, // Double space
        `Lesson ${chapter}.${lesson} `,  // Single space
      ];
      
      for (const pattern of patterns) {
        try {
          // Try to list directory contents to find files
          const response = await fetch(`/content/`);
          if (response.ok) {
            const html = await response.text();
            const linkMatches = html.match(/href="([^"]*\.md)"/g);
            if (linkMatches) {
              const matchingFiles = linkMatches
                .map(match => decodeURIComponent(match.match(/href="([^"]*)"/)[1]))
                .filter(file => file.startsWith(pattern.trim()) && file.endsWith('.md'));
              discoveredFiles.push(...matchingFiles);
            }
          }
          break;
        } catch (err) {
          // Continue
        }
      }
    }
  }
  
  const uniqueFiles = [...new Set(discoveredFiles)];
  console.log('Discovered files:', uniqueFiles);
  return uniqueFiles;
};

export const useLessons = () => {
  const [lessons, setLessons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadLessons = async () => {
      try {
        setLoading(true);
        
        // Load lesson files from manifest (with fallback to discovery)
        const discoveredFiles = await loadLessonManifest();
        console.log('Discovered lesson files:', discoveredFiles);
        
        const lessonPromises = discoveredFiles.map(async (filename) => {
          try {
            const response = await fetch(`/content/${filename}`);
            if (!response.ok) {
              throw new Error(`Failed to fetch ${filename}: ${response.status}`);
            }
            const content = await response.text();
            const parsed = parseMarkdown(content);
            
            return {
              ...parsed,
              filename: filename.replace('.md', '')
            };
          } catch (err) {
            console.error(`Error loading ${filename}:`, err);
            return null;
          }
        });

        const loadedLessons = await Promise.all(lessonPromises);
        const validLessons = loadedLessons.filter(lesson => lesson !== null);
        
        setLessons(sortLessonsByNumber(validLessons));
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    loadLessons();
  }, []);

  const lessonNavigation = useMemo(() => {
    const navigation = {};
    lessons.forEach(lesson => {
      const slug = lesson.frontmatter.slug;
      navigation[slug] = {
        next: getNextLesson(lessons, slug),
        previous: getPreviousLesson(lessons, slug)
      };
    });
    return navigation;
  }, [lessons]);

  const getLessonBySlug = (slug) => {
    return lessons.find(lesson => lesson.frontmatter.slug === slug);
  };

  return {
    lessons,
    loading,
    error,
    lessonNavigation,
    getLessonBySlug
  };
};